/**
 * ארכיון הצעות — TASKS 5.6, PRD 8.2. רשימה + סינון לפי תאריך ולקוח, עימוד שרת.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { AdminNav } from './AdminNav.js';
import { AdminPageHeader } from './AdminPageHeader.js';
import { isAdminApiError } from './adminApi.js';
import { listQuotes, type QuoteListItem } from './quotesApi.js';
import { WhatsAppSendButton } from './WhatsAppSendButton.js';

const PRICE_BASIS_LABEL: Record<QuoteListItem['price_basis'], string> = {
  base: 'מחיר בסיס',
  customer: 'מחיר מיוחד',
  manual: 'ידני',
};

const SOURCE_LABEL: Record<QuoteListItem['source'], string> = {
  site: 'אתר',
  admin: 'ידני',
};

export function QuotesPage() {
  const [quotes, setQuotes] = useState<QuoteListItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [q, setQ] = useState('');
  const [listError, setListError] = useState<string | null>(null);

  async function refetch() {
    setListError(null);
    try {
      const result = await listQuotes({
        page,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        q: q.trim() || undefined,
      });
      setQuotes(result.quotes);
      setTotal(result.total);
      setPageSize(result.pageSize);
    } catch (err) {
      setListError(isAdminApiError(err) ? err.message : 'שגיאה בטעינת ההצעות.');
    }
  }

  useEffect(() => {
    void refetch();
  }, [page]);

  function handleFilterSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPage(1);
    void refetch();
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <>
      <AdminNav />
      <main className="mx-auto max-w-6xl px-6 py-12">
        <AdminPageHeader
          title="ארכיון הצעות"
          description="כל הצעות המחיר שנשלחו, מהאתר ומהפאנל."
          action={
            <Link to="/admin/quotes/new" className="btn btn-cta">
              הצעה ידנית חדשה
            </Link>
          }
        />

        <form
          className="mt-6 grid gap-4 sm:grid-cols-[1fr_1fr_2fr_auto]"
          onSubmit={handleFilterSubmit}
          role="search"
        >
          <div>
            <label htmlFor="q-date-from" className="block text-sm font-semibold">
              מתאריך
            </label>
            <input
              id="q-date-from"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="mt-1 w-full rounded-md border border-line px-3 py-2"
            />
          </div>
          <div>
            <label htmlFor="q-date-to" className="block text-sm font-semibold">
              עד תאריך
            </label>
            <input
              id="q-date-to"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="mt-1 w-full rounded-md border border-line px-3 py-2"
            />
          </div>
          <div>
            <label htmlFor="q-search" className="block text-sm font-semibold">
              חיפוש (מספר הצעה, שם או טלפון לקוח)
            </label>
            <div className="relative mt-1">
              <MagnifyingGlass
                size={16}
                weight="regular"
                aria-hidden="true"
                className="text-muted pointer-events-none absolute start-3 top-1/2 -translate-y-1/2"
              />
              <input
                id="q-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="w-full rounded-md border border-line py-2 pe-3 ps-9"
              />
            </div>
          </div>
          <div className="flex items-end">
            <button type="submit" className="btn btn-secondary">
              סינון
            </button>
          </div>
        </form>

        {listError && (
          <p role="alert" className="text-danger mt-4 text-sm">
            {listError}
          </p>
        )}

        {quotes === null && !listError && <p className="text-muted mt-6">טוען…</p>}
        {quotes && quotes.length === 0 && <p className="text-muted mt-6">לא נמצאו הצעות.</p>}

        {quotes && quotes.length > 0 && (
          <>
            <div className="mt-6 overflow-x-auto">
              <table className="admin-table w-full border-collapse text-sm">
                <caption className="sr-only">
                  ארכיון הצעות, עמוד {page} מתוך {totalPages}
                </caption>
                <thead>
                  <tr className="border-line border-b text-start">
                    <th scope="col" className="p-2 text-start">
                      מספר הצעה
                    </th>
                    <th scope="col" className="p-2 text-start">
                      לקוח
                    </th>
                    <th scope="col" className="p-2 text-start">
                      סכום כולל מע&quot;מ
                    </th>
                    <th scope="col" className="p-2 text-start">
                      מקור מחיר
                    </th>
                    <th scope="col" className="p-2 text-start">
                      ערוץ
                    </th>
                    <th scope="col" className="p-2 text-start">
                      נוצרה
                    </th>
                    <th scope="col" className="p-2 text-start">
                      פעולות
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {quotes.map((quote) => (
                    <tr key={quote.id} className="border-line border-b align-top">
                      <td className="p-2">
                        <span className="ltr-inline">{quote.quote_number}</span>
                      </td>
                      <td className="p-2">
                        {quote.customer_full_name}
                        <br />
                        <span className="ltr-inline text-muted">{quote.customer_phone_e164}</span>
                      </td>
                      <td className="tabular p-2">₪{quote.total}</td>
                      <td className="p-2">{PRICE_BASIS_LABEL[quote.price_basis]}</td>
                      <td className="p-2">{SOURCE_LABEL[quote.source]}</td>
                      <td className="p-2">
                        {new Date(quote.created_at).toLocaleDateString('he-IL')}
                      </td>
                      <td className="p-2">
                        <div className="flex flex-col items-start gap-2">
                          <Link
                            to={`/admin/quotes/${quote.id}`}
                            className="rounded border border-line px-2 py-1"
                          >
                            פרטים
                          </Link>
                          {Boolean(quote.has_pdf) && (
                            <WhatsAppSendButton
                              quoteId={quote.id}
                              quoteNumber={quote.quote_number}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <nav aria-label="עימוד ארכיון הצעות" className="mt-4 flex items-center gap-3">
              <button
                type="button"
                className="rounded border border-line px-3 py-1 disabled:opacity-40"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                הקודם
              </button>
              <span className="text-sm">
                עמוד {page} מתוך {totalPages} ({total} הצעות)
              </span>
              <button
                type="button"
                className="rounded border border-line px-3 py-1 disabled:opacity-40"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                הבא
              </button>
            </nav>
          </>
        )}
      </main>
    </>
  );
}
